import moment from 'moment';
import momentTz from 'moment-timezone';
import { getPermissions } from './permissions';
import store from '../store/configureStore';

// Reinterpret an epoch's wall-clock time in the org timezone (for SENDING).
// The pickers produce browser-local epochs; the backend expects the same
// wall-clock interpreted in the org timezone. Falls back to the original
// epoch (browser behaviour) when no org timezone is configured.
export const toOrgEpoch = (epochMillis: number, tz?: string | null): number =>
    tz
        ? momentTz
              .tz(momentTz(epochMillis).format('YYYY-MM-DDTHH:mm:ss'), tz)
              .valueOf()
        : epochMillis;

// Inverse: convert an org-timezone epoch into a browser-local epoch with the
// same wall-clock, so edit prefill and listings show the correct time and
// resaving does not double-shift.
export const fromOrgEpoch = (
    epochMillis: number,
    tz?: string | null,
): number =>
    tz
        ? momentTz(
              momentTz.tz(epochMillis, tz).format('YYYY-MM-DDTHH:mm:ss'),
          ).valueOf()
        : epochMillis;

// Format an org-timezone epoch in the org's wall-clock time. Reads the org
// timezone from the store, so callers that aren't redux-connected can use it.
export const formatOrgTime = (
    epochMillis?: number | null,
    format = 'HH:mm',
): string => {
    if (!epochMillis) return '-';
    const tz = store?.getState()?.user?.config?.timezone;
    return moment(fromOrgEpoch(epochMillis, tz)).format(format);
};

export function formatCurrencyV1(value: number | string, opt = {}) {
    let options = {
        style: 'currency',
        currency: 'INR',
        significantDigits: 2,
        thousandsSeparator: ',',
        decimalSeparator: '.',
        symbol: '₹',
    };
    if (typeof value !== 'number') value = 0.0;
    options = { ...options, ...opt };
    let actual = value;
    value = Number(value) < 0 ? Math.abs(value) : value;
    value = value?.toFixed(options?.significantDigits);

    const [currency, decimal] = value?.split('.');
    return Number(actual) < 0
        ? `- ${options?.symbol}${currency?.replace(
              /\B(?=(\d{3})+(?!\d))/g,
              options?.thousandsSeparator,
          )}${decimal ? `.${decimal}` : ``}`
        : `${options?.symbol}${currency?.replace(
              /\B(?=(\d{3})+(?!\d))/g,
              options?.thousandsSeparator,
          )}${decimal ? `.${decimal}` : ``}`;
}

export const currencyFormatWithoutDecimal = {
    style: 'currency',
    currency: 'INR',
    significantDigits: 0,
    thousandsSeparator: ',',
    decimalSeparator: '.',
    symbol: '₹',
};

export const getEpoch = (time: string): number => {
    if (!time) return 0;
    // Ensure the time is in HH:mm:ss format
    const today = moment().format('YYYY-MM-DD');
    return moment(`${today} ${time}`)?.valueOf();
};

export const FormatTime = (time: string): string => {
    if (!time) return '';
    return moment(getEpoch(time)).format('hh:mm a');
};

export const BBNOW_VENDOR_ID = ['25', 25, 29, '29'];

export const isBlueDartUser = () => {
    return (
        !!getPermissions('rider_management')?.read ||
        !!getPermissions('ops_management')?.read ||
        !!getPermissions('vendor_management')?.read
    );
};
/**
 * MPIN rollout flag from /common/configs (state.user.config.mpinEnabled).
 * Gates the MPIN admin action on the staff/rider listings; tolerant of the
 * backend sending a boolean or a "true" string.
 */
export const isMpinEnabled = (): boolean => {
    const flag = store?.getState()?.user?.config?.mpinEnabled;
    return flag === true || String(flag).toLowerCase() === 'true';
};

export const isBlueDartOrg = () => {
    const state = store?.getState();
    return (
        state?.settings?.basicDetails?.vendorName === 'BLUEDART' ||
        state?.settings?.basicDetails?.operatingCityId === 1000 ||
        isBlueDartVendorRole()
    );
};

const BD_SUPER_ADMIN_ROLES: Array<string | number> = [30, '30'];
const BD_RIDER_APPROVER_ROLES: Array<string | number> = [31, '31'];
const BD_ZONAL_APPROVER_ROLES: Array<string | number> = [49, '49'];
const BD_RIDER_VIEWER_ROLES: Array<string | number> = [48, '48'];

/**
 * BD internal roles carrying WRITE authority. Mirrors BluedartUtil.isBluedartOpsRole on the
 * backend, including its exclusion of Rider Viewer — this set gates real actions such as
 * Initiate/Retrigger BGV, which a read-only role must never see. Use BD_INTERNAL_ROLES for
 * anything that merely distinguishes "internal user" from "vendor".
 */
export const BD_OPS_ROLES: Array<string | number> = [
    ...BD_SUPER_ADMIN_ROLES,
    ...BD_RIDER_APPROVER_ROLES,
    ...BD_ZONAL_APPROVER_ROLES,
];

/** Any BD internal role, including read-only Rider Viewer. Mirrors isBluedartInternalRole. */
export const BD_INTERNAL_ROLES: Array<string | number> = [
    ...BD_OPS_ROLES,
    ...BD_RIDER_VIEWER_ROLES,
];

const VENDOR_ROLES: Array<string | number> = [33, '33'];
const EV_CAPTAIN_ROLES: Array<string | number> = [19, '19'];

/**
 * The backend now ships the resolved role NAME on /common/configs as `bdRole`, which is
 * authoritative. The numeric fallback keeps older sessions working until every client has a
 * config carrying it.
 */
export const getBdRole = (): string | null =>
    store?.getState()?.user?.config?.bdRole ?? null;

const hasBdRole = (
    name: string,
    numericRoles: Array<string | number>,
): boolean => {
    const bdRole = getBdRole();
    if (bdRole) return bdRole === name;
    const role = store?.getState()?.user?.role;
    return numericRoles.includes(role as string | number);
};

export const isBlueDartVendorRole = (): boolean => {
    const role = store?.getState()?.user?.role;
    return VENDOR_ROLES.includes(role as string | number);
};

export const isBlueDartOpsRole = (): boolean => {
    const bdRole = getBdRole();
    if (bdRole)
        return (
            bdRole !== 'BLUEDART_RIDER_VIEWER' && bdRole !== 'BLUEDART_VENDOR'
        );
    const role = store?.getState()?.user?.role;
    return BD_OPS_ROLES.includes(role as string | number);
};

/** Includes Rider Viewer. For read gates and for telling internal users apart from vendors. */
export const isBlueDartInternalRole = (): boolean => {
    const bdRole = getBdRole();
    if (bdRole) return bdRole !== 'BLUEDART_VENDOR';
    const role = store?.getState()?.user?.role;
    return BD_INTERNAL_ROLES.includes(role as string | number);
};

export const isBlueDartSuperAdmin = (): boolean =>
    hasBdRole('BLUEDART_SUPER_ADMIN', BD_SUPER_ADMIN_ROLES);

export const isBlueDartZonalApprover = (): boolean =>
    hasBdRole('BLUEDART_ZONAL_APPROVER', BD_ZONAL_APPROVER_ROLES);

export const isBlueDartRiderApprover = (): boolean =>
    hasBdRole('BLUEDART_RIDER_APPROVER', BD_RIDER_APPROVER_ROLES);

export const isBlueDartRiderViewer = (): boolean =>
    hasBdRole('BLUEDART_RIDER_VIEWER', BD_RIDER_VIEWER_ROLES);

/** 'GLOBAL' | 'STORE' | 'ZONE' — absent for non-BD users. */
export const getBdScopeType = (): string | null =>
    store?.getState()?.user?.config?.bdScopeType ?? null;

/** The caller's own stores or zones. Display and filter pre-population only — every list
 * re-resolves scope server-side, so this is never the gate. */
export const getBdScopeValues = (): string[] =>
    store?.getState()?.user?.config?.bdScopeValues ?? [];

export const isBdRbacScopingEnabled = (): boolean =>
    !!store?.getState()?.user?.config?.isBluedartRbacScopingEnabled;

export const isBdUserManagementTabEnabled = (): boolean =>
    !!store?.getState()?.user?.config?.isBluedartUserManagementTabEnabled;

export const isBdApprovalWorkflowEnabled = (): boolean =>
    !!store?.getState()?.user?.config?.isBluedartApprovalWorkflowEnabled;

export const isBdDeactivationEnabled = (): boolean =>
    !!store?.getState()?.user?.config?.isBluedartDeactivationReasonsEnabled;

export const isEvCaptain = (): boolean => {
    const role = store?.getState()?.user?.role;
    return EV_CAPTAIN_ROLES.includes(role as string | number);
};
export const isNonIndiaOrg = (): boolean => {
    const isNonIndiaOrg =
        !!store?.getState()?.user?.config?.country &&
        store?.getState()?.user.config.country.trim().toLowerCase() !== 'india';
    return isNonIndiaOrg;
};
export const isDocActionBlockedForRole = (
    approvalStatus: string | null | undefined,
): boolean => {
    const role = store?.getState()?.user?.role;
    // RB-8: Rider Viewer sees the documents but never the Approve/Reject controls, at any stage.
    // The two stage checks below only separate vendor from ops, so without this a Rider Viewer
    // would fall through to "not blocked" and be offered actions the backend refuses.
    if (isBlueDartRiderViewer()) {
        return true;
    }
    if (approvalStatus === 'PENDING_BD_OPS_APPROVAL') {
        return VENDOR_ROLES.includes(role as string | number);
    }
    if (approvalStatus === 'PENDING_VENDOR_APPROVAL') {
        return BD_OPS_ROLES.includes(role as string | number);
    }
    return false;
};

export interface DLPayload {
    documentNo?: string;
    docDate?: string;
    imageFront?: string | null;
    imageBack?: string | null;
    docDateMilliSeconds?: number;
}

export interface DLValidationResult {
    isValid: boolean;
    errorMessage: string | null;
    isUpdated: boolean; // <- NEW: true if any field changed
}

export const validateDL = (
    current: DLPayload | null | undefined | any,
    previous?: DLPayload | any,
): DLValidationResult => {
    if (!current) {
        return {
            isValid: false,
            errorMessage: 'Payload missing',
            isUpdated: false,
        };
    }

    const { documentNo, docDate, docDateMilliSeconds, imageFront, imageBack } =
        current;

    // 1. Basic Validations
    if (!documentNo || documentNo?.trim()?.length === 0) {
        return {
            isValid: false,
            errorMessage: 'Please enter a valid DL number',
            isUpdated: false,
        };
    }

    if (!docDateMilliSeconds) {
        return {
            isValid: false,
            errorMessage: 'Please enter a valid Date of Birth',
            isUpdated: false,
        };
    }

    // 2. Age Validation (18+)
    const dob = moment(docDateMilliSeconds);
    if (!dob?.isValid()) {
        return {
            isValid: false,
            errorMessage: 'Invalid date format. Please enter a valid date',
            isUpdated: false,
        };
    }

    const age = moment().diff(dob, 'years');
    if (age < 18) {
        return {
            isValid: false,
            errorMessage: 'Age must be 18 years or older',
            isUpdated: false,
        };
    }

    // 3. Image check
    if (!imageFront && !imageBack) {
        return {
            isValid: false,
            errorMessage: 'Please upload front or back image of the DL',
            isUpdated: false,
        };
    }

    // 4. Check for updates compared to previous payload
    let isUpdated = false;
    if (!!previous) {
        isUpdated =
            previous?.documentNo !== current?.documentNo ||
            previous?.docDateMilliSeconds !== current?.docDateMilliSeconds ||
            previous?.imageFront !== current?.imageFront ||
            previous?.imageBack !== current?.imageBack;
    } else {
        // If there is no previous payload → consider it new
        isUpdated = true;
    }

    return {
        isValid: true,
        errorMessage: null,
        isUpdated,
    };
};
export const getMaxDOBFor18Years = () => {
    return moment().subtract(18, 'years').valueOf();
};

export function buildIframeUrl(
    baseUrl: string,
    path: string = '',
    token: string,
) {
    const url = new URL(path, baseUrl);
    url.searchParams.set('authToken', token);
    return url?.toString();
}
export type DigiLockerVerificationResult = {
    isVerified: boolean;
    status: 'APPROVED' | 'REJECTED' | 'PENDING';
    broker: string;
    name?: string;
    documentNumber?: string;
    documentDob?: string;
    isDigilockerBroker: boolean;
};
export const KYC_BROKERS = Object.freeze({
    HYPERVERGE: 'HYPERVERGE',
    DIGI_LOCKER: 'BUREAU_ID_DIGILOCKER',
});
export function verifiedByDigilockerStatus(
    response: any,
): DigiLockerVerificationResult {
    const status =
        response?.approvalStatus === 'APPROVED'
            ? 'APPROVED'
            : response?.approvalStatus === 'REJECTED'
              ? 'REJECTED'
              : 'PENDING';

    const isVerified =
        status === 'APPROVED' &&
        response?.autoKycBroker === KYC_BROKERS.DIGI_LOCKER;

    return {
        isVerified,
        status,
        isDigilockerBroker: response?.autoKycBroker === KYC_BROKERS.DIGI_LOCKER,
        broker: response?.autoKycBroker ?? '',
        name: response?.autoKycDocumentName ?? '',
        documentNumber: response?.autoKycDocumentNo,
        documentDob: response?.autoKycDocumentDob,
    };
}

export const isDigilockerVerified = (response: any): boolean => {
    return verifiedByDigilockerStatus(response)?.isVerified;
};

const extractPathFromUrl = (link: string): string => {
    try {
        const url = new URL(link);
        return url.pathname || '';
    } catch {
        // Fallback for non-URL strings
        return link.split('#')[0].split('?')[0];
    }
};

export const isImageLink = (link?: string | null): boolean => {
    if (!link) return false;
    const trimmed = `${link}`?.trim();
    if (!trimmed) return false;

    // Handle data URLs for images
    if (trimmed?.startsWith('data:image/')) return true;

    const path = extractPathFromUrl(trimmed).toLowerCase();

    const imageExtensions = [
        '.jpg',
        '.jpeg',
        '.png',
        '.gif',
        '.bmp',
        '.webp',
        '.svg',
        '.tif',
        '.tiff',
    ];

    return imageExtensions.some((ext) => path.endsWith(ext));
};

export const isPdfLink = (link?: string | null): boolean => {
    if (!link) return false;

    const trimmed = `${link}`?.trim();
    if (!trimmed) return false;

    const lower = trimmed.toLowerCase();
    const path = extractPathFromUrl(lower);

    // Check by extension first (works well for S3/object URLs even with query params)
    if (path.endsWith('.pdf')) return true;

    // Fallback: some signed URLs encode the content type as a query param
    // e.g. response-content-type=application/pdf
    if (lower.includes('application/pdf')) return true;

    return false;
};

export const formatTimezone = (tz: any) => {
    if (!tz) return null;
    try {
        const now = new Date();
        const abbr = new Intl.DateTimeFormat('en', {
            timeZone: tz,
            timeZoneName: 'short',
        })
            .formatToParts(now)
            .find((p) => p.type === 'timeZoneName')?.value;
        const offsetRaw = new Intl.DateTimeFormat('en', {
            timeZone: tz,
            timeZoneName: 'shortOffset',
        })
            .formatToParts(now)
            .find((p) => p.type === 'timeZoneName')?.value;
        const utcOffset = offsetRaw?.replace('GMT', 'UTC') ?? '';
        return `${abbr} (${utcOffset}) ${tz}`;
    } catch {
        return tz;
    }
};

// Vendors paste document numbers straight off the card .trim() leaves the inner
// spaces, and they travel all the way through to BlueDart's employee API.
export const stripDocumentNumberSpaces = (value?: string | null): string =>
    (value ?? '').replace(/\s+/g, '');
